/**
 * The words sent to somebody's own agent when a screen asks it to sync a project's documentation
 * into DevPulse.
 *
 * This is the first thing a new project needs: DevPulse starts empty, the writing already exists in
 * the repository, and the one thing that can read a repository is the agent working in it. So the
 * SYNC button asks it to mirror what is there — and says, in the command itself, what must not come
 * along: code, generated output, anything holding a key.
 */

import type { SpecSummary } from './types'

export function syncDocsPrompt(project: { key: string; name: string }, specs: SpecSummary[]): string {
  const folder = project.key.toLowerCase()

  const known = specs.length
    ? specs
        .map((spec) => `- ${spec.kind === 'Prd' ? 'PRD' : 'SDD'} ${spec.key}: ${spec.title}${spec.documentPath ? ` (${spec.documentPath})` : ''}`)
        .join('\n')
    : '- (Vephus knows no SDD or PRD for this project yet.)'

  return [
    `Sync the documentation of the project ${project.key} (${project.name}) into Vephus.`,
    '',
    'Vephus already knows about:',
    known,
    '',
    'Do this:',
    `1. List what is in Vephus first (devpulse_docs_list), so an existing document is updated, not duplicated.`,
    '2. Find this project\'s written artefacts in the repository you have open: the README, docs/,',
    '   specifications, SDDs, PRDs, ADRs, runbooks — the documents a new person would be given.',
    `3. Write each one with devpulse_docs_write, under "${folder}/", keeping the repository's own folder`,
    '   structure and file names (Markdown only, .md).',
    '4. Link a document to the bug, task or specification it is about with devpulse_docs_link.',
    '   devpulse_bugs_list shows the open bugs if you need their tickets.',
    '5. Leave out anything that is not documentation: source code, lock files, generated output,',
    '   dependencies, and anything holding a key, token or password. Vephus refuses those anyway.',
    '6. If a document describes work Vephus has no record of, say so in your reply — do not invent',
    '   tickets or specifications.',
    '',
    'Then reply with one short paragraph: how many documents you wrote, how many you updated, and what',
    'you deliberately left out.',
  ].join('\n')
}
